"use client";

import React from "react";
import { SlidersHorizontal, ShieldCheck, ArrowRight, FileText, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  badgeCategory: string;
  title: string;
  lead: string;
  outlineCta: string;
  imageAlt: string;
  imageOverlay: string;
  cardNote: string;
  cardLink: string;
}
const copy: Record<Lang, HeroCopy> = {
  en: {
    badgeCategory: "Woven Wire Mesh Specifications",
    title: "Industrial Wire Mesh Specifications & Engineering Parameters",
    lead: "Configure mesh count, aperture opening, wire diameter, and open screening area across precision industrial weaves. Technical ranges calibrated for flow rate and particle separation requirements.",
    outlineCta: "Mesh Count & Aperture",
    imageAlt: "Macro view of stainless steel woven wire mesh weave structure and precision aperture openings",
    imageOverlay: "ASTM E2016 / ISO 9044",
    cardNote: "Square & Dutch industrial weaves",
    cardLink: "View charts"
  },
  zh: {
    badgeCategory: "编织丝网技术规格",
    title: "工业金属丝网技术规格与工程参数",
    lead: "精准配置目数、开孔孔径、金属丝径及开孔率，严格符合精密工业编织规范。各项技术公差与技术范围适配流体通量与颗粒分离工况。",
    outlineCta: "目数与孔径规格表",
    imageAlt: "不锈钢编织丝网微观编织结构与精密开孔细节",
    imageOverlay: "ASTM E2016 / ISO 9044",
    cardNote: "方孔与密纹工业编织结构",
    cardLink: "查看图表"
  },
  es: {
    badgeCategory: "Especificaciones de malla metálica",
    title: "Especificaciones y parámetros de ingeniería de malla metálica",
    lead: "Configure el número de mallas, abertura, diámetro de alambre y área abierta en tejidos industriales de precisión. Rangos técnicos calibrados para caudal y separación de partículas.",
    outlineCta: "Mallas y aberturas",
    imageAlt: "Detalle macro de la estructura de tejido de malla de alambre de acero inoxidable y aberturas de precisión",
    imageOverlay: "ASTM E2016 / ISO 9044",
    cardNote: "Tejidos industriales cuadrados y Dutch",
    cardLink: "Ver tablas"
  },
  de: {
    badgeCategory: "Drahtgewebe-Spezifikationen",
    title: "Spezifikationen und Konstruktionsparameter für Industriedrahtgewebe",
    lead: "Konfigurieren Sie Maschenzahl, Maschenweite, Drahtdurchmesser und offene Siebfläche für präzise Industriegewebe. Technische Bereiche abgestimmt auf Durchfluss und Partikeltrennung.",
    outlineCta: "Maschen & Maschenweiten",
    imageAlt: "Nahaufnahme der Gewebestruktur von Edelstahldrahtgewebe und präzisen Maschenöffnungen",
    imageOverlay: "ASTM E2016 / ISO 9044",
    cardNote: "Quadrat- und Tressengewebe für die Industrie",
    cardLink: "Tabellen anzeigen"
  },
  fr: {
    badgeCategory: "Spécifications des toiles métalliques",
    title: "Spécifications et paramètres techniques des toiles métalliques",
    lead: "Définissez le nombre de mailles, l'ouverture, le diamètre de fil et la surface ouverte sur des tissages industriels de précision. Plages techniques adaptées au débit et à la séparation.",
    outlineCta: "Maillages et ouvertures",
    imageAlt: "Vue macro de la structure de tissage de toile métallique en acier inoxydable et ouvertures de précision",
    imageOverlay: "ASTM E2016 / ISO 9044",
    cardNote: "Tissages industriels carrés et hollandais",
    cardLink: "Voir les tableaux"
  },
  pt: {
    badgeCategory: "Especificações de malha de arame",
    title: "Especificações e parâmetros técnicos de malha metálica industrial",
    lead: "Configure contagem de malha, abertura, diâmetro do arame e área aberta em tecelagens industriais de precisão. Parâmetros técnicos calibrados para vazão e retenção de partículas.",
    outlineCta: "Malhas e aberturas",
    imageAlt: "Visão macro da estrutura de tecelagem de malha de aço inoxidável e aberturas de precisão",
    imageOverlay: "ASTM E2016 / ISO 9044",
    cardNote: "Tecelagens industriais quadradas e holandesas",
    cardLink: "Ver tabelas"
  }
};
interface SpecItem {
  label: Record<Lang, string>;
  value: string;
}
const specStripData: SpecItem[] = [{
  label: {
    en: "Square Mesh Range",
    zh: "方孔目数范围",
    es: "Rango de malla cuadrada",
    de: "Quadratmaschen-Bereich",
    fr: "Gamme maille carrée",
    pt: "Faixa de malha quadrada"
  },
  value: "1 – 635 Mesh"
}, {
  label: {
    en: "Wire Diameters",
    zh: "金属丝径范围",
    es: "Diámetros de alambre",
    de: "Drahtdurchmesser",
    fr: "Diamètres de fil",
    pt: "Diâmetros de arame"
  },
  value: "0.020 – 2.0 mm"
}, {
  label: {
    en: "Aperture Openings",
    zh: "孔径公称范围",
    es: "Abertura de malla",
    de: "Maschenweiten",
    fr: "Ouvertures de maille",
    pt: "Aberturas de malha"
  },
  value: "20 µm – 25 mm"
}, {
  label: {
    en: "Governing Standards",
    zh: "执行制造标准",
    es: "Normas de fabricación",
    de: "Fertigungsnormen",
    fr: "Normes de référence",
    pt: "Normas de fabricação"
  },
  value: "ISO 9044 / ASTM E2016"
}];
export default function SpecificationsHero() {
  const {
    lang
  } = useLanguage();
  const t = pick(lang, copy);
  return <section id="specificationshero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 cols on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Technical Badges: EXACTLY two outline badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{t.badgeCategory}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>ISO 9044 / ASTM E2016</span>
              </Badge>
            </div>

            {/* Page single H1 */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {t.title}
            </h1>

            {/* Lead */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {t.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-6 font-semibold">
                <Link to="/MeshCountAndAperturePage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{t.outlineCta}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Hero Spec Card (5 cols on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="relative mf-card-media mf-ratio-hero w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-overhead-photograph-of-stainless-s-ff326f.png" alt={t.imageAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{t.imageOverlay}</span>
                </div>
              </div>

              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{t.cardNote}</span>
                  </div>
                  <Link to="/TechnicalChartsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{t.cardLink}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {specStripData.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{pick(lang, spec.label)}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}